import { createAction, createReducer } from '@reduxjs/toolkit';
import { initial } from 'lodash';
import { getLocalStorage } from '../utils/helpers';

import {
    FETCH_MESSAGES,
    FETCH_MESSAGES_SUCCESS,
    FETCH_MESSAGES_ERROR,
    UPDATE_MESSAGE,
    UPDATE_FILES,
    UPDATE_FILES_SUCCESS,
    SEND_MESSAGE,
    SEND_MESSAGE_SUCCESS,
    SEND_MESSAGE_ERROR,
    FETCH_RECIPIENTS,
    FETCH_RECIPIENTS_SUCCESS,
    FETCH_RECIPIENTS_ERROR,
    FILTER_MESSAGES,
    FILTER_MESSAGES_CLEAR,
    FETCH_RECIPIENTS_PAGINATE_SUCCESS,
    SET_MESSAGE_CONFIG,
} from '../constants/ActionTypes';

const emptyFilters = {
    show_archived: false,
    show_starred: false,
    direct_quotes: false,
    unread_messages: false,
    customer_messaged_last: false,
    bookings: false,
    quotes: false,
    meal_date: '',
    name: '',
};

const initialState = {
    loading: false,
    hasLoaded: false,
    config: {
        hasInit: false,
        resourceId: false,
        chefId: false,
        type: false,
        isCustomer: false,
        recipientId: false,
        future: true,
        status: false,
        chef_slug: '',
        last_seen: false,
        conversationId: false,
        showExistingConversations: true,
    },
    messageList: {
        errors: [],
        messages: [],
        quote: false,
        order: false,
        conversation: false,
        pagination: {
            links: false,
            meta: false,
        },
    },
    newMessage: {
        body: '',
        uploads: {
            files: [],
            loading: false,
            hasLoaded: true,
        },
        sending: false,
        errors: false,
    },
    recipients: {
        loading: false,
        hasLoaded: false,
        errors: [],
        upcomingOrders: [],
        users: [],
        upcoming: [],
        past: [],
        filters: emptyFilters,
        selectedFilters: emptyFilters,
        newConversations: false,
        pagination: {
            links: false,
            meta: false,
        },
    },
};

export const messages = createReducer(initialState, (builder) => {
    builder
        .addCase('INIT_MESSAGES', (state, action) => {
            state.config.chefId = action.payload.chefId;
            state.config.resourceId = action.payload.resourceId;
            state.config.type = action.payload.type;
            state.config.recipientId = action.payload.recipientId;
            state.config.conversationId = action.payload.conversationId;
            state.config.hasInit = true;
        })
        .addCase('FETCH_MESSAGES', (state) => {
            // Refetching the conversation already on screen keeps the current
            // list until fresh data lands (no skeleton flash). Loose equality
            // on purpose: ids can arrive as string or number.
            const loadedConversationId = state.messageList.conversation?.id;

            if (
                loadedConversationId &&
                state.config.conversationId == loadedConversationId
            ) {
                return;
            }

            // Fetching a different (or first) conversation clears immediately
            // so the previous thread never bleeds into the new one.
            state.loading = true;
            state.hasLoaded = false;
            state.messageList = initialState.messageList;
        })
        .addCase('FETCH_MESSAGES_SUCCESS', (state, action) => {
            // Drop stale responses: a fetch that resolves after the user has
            // already switched to a different conversation must not overwrite
            // the newer thread (or rewind config.conversationId to the old
            // one). Loose equality on purpose: ids can arrive as string or
            // number. Responses without a conversation id (resource/embed
            // mounts) keep the previous behaviour.
            const incomingConversationId = action.payload?.conversation?.id;

            if (
                incomingConversationId &&
                state.config.conversationId &&
                incomingConversationId != state.config.conversationId
            ) {
                return;
            }

            let messages = action.payload?.messages;
            let isPaginated = action.payload?.isPaginated;
            state.loading = false;
            state.hasLoaded = true;
            state.config.isCustomer = action.payload?.isCustomer;

            if (isPaginated) {
                // Offset pages shift whenever a message is sent between
                // paginations, so an older page can re-serve rows already
                // on screen. Union by id so they don't duplicate.
                const existingIds = new Set(
                    state.messageList.messages.map((message) => message.id)
                );

                state.messageList.messages = [
                    ...messages.data
                        .reverse()
                        .filter((message) => !existingIds.has(message.id)),
                    ...state.messageList.messages,
                ];
            } else {
                // A same-conversation refetch (echo event, payment window
                // callback) must not swallow optimistic rows that are still
                // sending or failed; the send ack reconciles or drops them.
                const pendingRows = state.messageList.messages.filter(
                    (message) => message.sending || message.failed
                );

                state.messageList.messages = [
                    ...messages.data.reverse(),
                    ...pendingRows,
                ];
                state.messageList.order = action.payload?.order;
                state.messageList.quote = action.payload?.quote;
                state.messageList.conversation = action.payload?.conversation;

                state.config.conversationId =
                    action.payload?.conversation?.id;

                if (messages.data.length > 0) {
                    let lastMessage = messages.data.slice(-1).pop();
                    state.config.resourceId = lastMessage?.order_id
                        ? lastMessage?.order_id
                        : lastMessage?.quote_id;
                    state.config.type = lastMessage?.order_id
                        ? 'order'
                        : 'quote';
                    state.config.chefId = lastMessage?.chef_id;
                    state.config.chef_slug = lastMessage?.chef_slug;
                    state.config.last_seen = state.config.isCustomer
                        ? lastMessage?.chef_last_seen
                        : lastMessage?.customer_last_seen;

                    const today = new Date().setHours(0, 0, 0, 0),
                        orderDate = new Date(
                            action.payload?.order?.meal_date
                        ).setHours(0, 0, 0, 0);
                    state.config.future = orderDate < today ? false : true;
                    state.config.status = action.payload?.order?.order_status;
                }
            }

            state.messageList.errors = action.payload?.errors;

            state.messageList.pagination = {
                links: messages?.links,
                meta: messages?.meta,
            };
        })
        .addCase('FETCH_MESSAGES_ERROR', (state, action) => {
            state.loading = false;
            state.hasLoaded = true;
            state.messageList.errors = action.payload?.errors;
        })
        .addCase('UPDATE_MESSAGE', (state, action) => {
            state.newMessage.body = action.payload;
        })
        .addCase('UPDATE_FILES', (state, action) => {
            state.newMessage.uploads.files =
                initialState.newMessage.uploads.files;
            state.newMessage.uploads.loading = true;
            state.newMessage.uploads.hasLoaded = false;
        })
        .addCase('UPDATE_FILES_SUCCESS', (state, action) => {
            state.newMessage.uploads.files = action.payload;
            state.newMessage.uploads.loading = false;
            state.newMessage.uploads.hasLoaded = true;
        })
        .addCase('SEND_MESSAGE', (state, action) => {
            state.newMessage.sending = true;
            state.newMessage.errors = false;

            const tempMessage = action.payload?.tempMessage;
            if (!tempMessage) return;

            const existing = state.messageList.messages.find(
                (message) => message.id === tempMessage.id
            );

            if (existing) {
                // Retry of a failed optimistic row: flip it back to sending
                // in place, leaving the composer draft alone.
                existing.sending = true;
                existing.failed = false;
                return;
            }

            // Optimistic append: the message shows instantly and the
            // composer clears without waiting for the server ack.
            state.messageList.messages.push(tempMessage);

            if (action.payload?.clearComposer) {
                state.newMessage.body = '';
            }
            state.newMessage.uploads.files = [];
        })
        .addCase('SEND_MESSAGE_SUCCESS', (state, action) => {
            let messages = action.payload?.messages;
            const tempId = action.meta?.tempId;

            // Same stale-response idiom as FETCH_MESSAGES_SUCCESS: an ack
            // for a conversation the user has already switched away from
            // must not merge into the newly opened thread. Loose equality
            // on purpose: ids can arrive as string or number.
            const ackConversationId = messages?.data?.[0]?.conversation_id;

            const isStaleAck =
                ackConversationId &&
                state.config.conversationId &&
                ackConversationId != state.config.conversationId;

            if (isStaleAck) {
                // The thread was already reset when the other conversation
                // was fetched; drop any leftover optimistic row and settle
                // the send state.
                if (tempId) {
                    state.messageList.messages =
                        state.messageList.messages.filter(
                            (message) => message.id !== tempId
                        );
                }
            } else {
                // Reconcile to server truth without dropping older loaded
                // pages: keep every on-screen row the response page doesn't
                // cover (union by id), drop the acked temp row, and append
                // the server's latest page as the authoritative tail.
                const incoming = messages?.data
                    ? messages.data.reverse()
                    : [];
                const incomingIds = new Set(
                    incoming.map((message) => message.id)
                );

                const retained = state.messageList.messages.filter(
                    (message) =>
                        message.id !== tempId &&
                        !incomingIds.has(message.id)
                );

                // Optimistic rows that are still sending/failed are the
                // newest user actions, so they stay pinned after the
                // server page rather than drifting above it.
                const pendingRows = retained.filter(
                    (message) => message.sending || message.failed
                );
                const settledRows = retained.filter(
                    (message) => !(message.sending || message.failed)
                );

                state.messageList.messages = [
                    ...settledRows,
                    ...incoming,
                    ...pendingRows,
                ];

                // Deliberately do NOT refresh pagination from a send ack:
                // the /messages/store paginator's links point back at the
                // send endpoint itself, so adopting them poisons the next
                // infinite-scroll fetch (fetchMessages uses links.next
                // verbatim). The pre-send cursor from /messages/get stays
                // correct - older loaded pages survive via the id-union
                // above, and paginated fetches dedupe by id as offsets
                // shift after sends.
            }

            state.newMessage.sending = false;
            state.newMessage.errors = false;
        })
        .addCase('RECEIVE_MESSAGE', (state, action) => {
            // ENG-679: an incoming echo event carries the recipient-ready
            // row, so the thread appends it and the sidebar updates in
            // place - no thread refetch, no recipients re-pagination.
            // isOpenConversation = the event targets the conversation on
            // screen; appendToThread additionally requires that thread to
            // be settled (not mid-fetch) so the append cannot be clobbered.
            const incoming = action.payload?.message;
            const isOpenConversation = action.payload?.isOpenConversation;
            const appendToThread = action.payload?.appendToThread;

            if (!incoming?.id) return;

            if (appendToThread) {
                // Same id-union discipline as the fetch/send reconcilers:
                // a row already on screen (duplicate event, second tab)
                // must not append twice.
                const exists = state.messageList.messages.some(
                    (message) => message.id === incoming.id
                );

                if (!exists) {
                    // Optimistic rows that are still sending/failed stay
                    // pinned to the tail (ENG-676), so the realtime row
                    // lands before them.
                    const pendingRows = state.messageList.messages.filter(
                        (message) => message.sending || message.failed
                    );
                    const settledRows = state.messageList.messages.filter(
                        (message) => !(message.sending || message.failed)
                    );

                    // The viewer is looking at the thread and the
                    // conversation-scoped mark-as-read fires alongside the
                    // append, so the row lands already seen.
                    state.messageList.messages = [
                        ...settledRows,
                        { ...incoming, seen: true },
                        ...pendingRows,
                    ];

                    // Keep the header's activity indicator fresh,
                    // mirroring FETCH_MESSAGES_SUCCESS.
                    state.config.last_seen = state.config.isCustomer
                        ? incoming.chef_last_seen
                        : incoming.customer_last_seen;
                }
            }

            // Sidebar: refresh the conversation's row in place (preview,
            // timestamp, unread state) and bump it to the top - replacing
            // the per-event get-recipients re-pagination. A conversation
            // missing from the loaded list falls back to fetchRecipients
            // in the caller. Loose equality on purpose: ids can arrive as
            // string or number.
            const users = state.recipients.users;

            if (!Array.isArray(users)) return;

            const index = users.findIndex(
                (user) => user.conversation_id == incoming.conversation_id
            );

            if (index === -1) return;

            const row = users[index];
            row.body = incoming.body;
            row.created_at = incoming.sent;
            row.content_type = incoming.content_type;
            // The open conversation is being read right now (the scoped
            // mark-as-read, or the mid-fetch fallback's fetch chain, marks
            // it server-side); anything else becomes unread. Keeping the
            // open row seen also stops UserList's unseen-row lazy refetch.
            row.seen = !!isOpenConversation;

            users.splice(index, 1);
            users.unshift(row);
        })
        .addCase('SEND_MESSAGE_ERROR', (state, action) => {
            state.newMessage.sending = false;
            state.newMessage.errors = action.payload?.data?.errors || { message: [action.payload?.data?.message || 'Something went wrong, please try again.'] };

            // Mark the optimistic row as failed (text stays visible) so the
            // bubble can offer a one-tap retry.
            const tempId = action.meta?.tempId;
            if (!tempId) return;

            const tempRow = state.messageList.messages.find(
                (message) => message.id === tempId
            );

            if (tempRow) {
                tempRow.sending = false;
                tempRow.failed = true;
            }
        })
        .addCase('FETCH_RECIPIENTS', (state, action) => {
            state.recipients.loading = true;

            state.recipients.hasLoaded = action.payload.hasLoaded;
        })
        .addCase('FETCH_RECIPIENTS_SUCCESS', (state, action) => {
            state.recipients.loading = false;
            state.recipients.hasLoaded = true;
            state.recipients.errors = initialState.recipients.errors;
            state.config.isCustomer = action.payload?.isCustomer;
            state.recipients.upcomingOrders = action.payload?.upcomingOrders;
            state.recipients.newConversations =
                action.payload?.newConversations;

            // localStorage.setItem('yh-message-upcoming-orders', JSON.stringify(upcomingOrders));

            // let selectedUser = state.recipients.users.find((user) => return user.id === state.config.recipientId; })
            // console.log({ selectedUser})

            const users = action.payload?.users?.data;
            state.recipients.users = users;
            // localStorage.setItem('yh-message-users', JSON.stringify(users));

            // const upcomingUsers = action.payload?.users?.data.filter((user) => {
            //     return user.upcoming;
            // });
            // state.recipients.upcoming = upcomingUsers;
            // localStorage.setItem('yh-message-upcoming-users', JSON.stringify(upcomingUsers));

            // const pastUsers = action.payload?.users?.data.filter((user) => {
            //     return !user.upcoming;
            // });
            // state.recipients.past = pastUsers;
            // localStorage.setItem('yh-message-past-users', JSON.stringify(pastUsers));
            state.recipients.pagination = {
                ...state.pagination,
                links: action.payload.users?.links,
                meta: action.payload.users?.meta,
            };
        })
        .addCase('FETCH_RECIPIENTS_PAGINATE_SUCCESS', (state, action) => {
            state.recipients.loading = false;
            state.recipients.hasLoaded = true;
            state.recipients.errors = initialState.recipients.errors;
            const users = action.payload?.users?.data;

            state.recipients.users = [...state.recipients.users, ...users];

            state.recipients.pagination = {
                ...state.pagination,
                links: action.payload.users?.links,
                meta: action.payload.users?.meta,
            };
        })
        .addCase('FETCH_RECIPIENTS_ERROR', (state, action) => {
            state.recipients.loading = false;
            state.recipients.hasLoaded = true;
            state.recipients.errors = action.payload?.errors;
        })
        .addCase('FILTER_MESSAGES', (state, action) => {
            state.recipients.selectedFilters[action.payload.filter] =
                action.payload.value;
        })
        .addCase('FILTER_MESSAGES_CLEAR', (state, action) => {
            state.recipients.selectedFilters = emptyFilters;
        })
        .addCase('SET_MESSAGE_CONFIG', (state, action) => {
            // toggle showExistingConversations to the opposite of what it is
            state.config[action.payload.key] = action.payload.value;
        });
});
